import { useEffect, useState } from 'react';
import { Alert, Box, Card, CardContent, CircularProgress, Typography } from '@mui/material';
import api from '../services/api';

const MESES_CURTOS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const MESES_LONGOS = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

const AZUL = '#13375F';
const CINZA_CABECALHO = '#6b7280';
const CINZA_ROTULO = '#e5e7eb';
const BORDA = '1px solid #cfd4dc';

const formatar = (n) => Number(n || 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 });

// Uma célula de número (dias): o planejado em destaque e, abaixo e menor, o realizado na Agenda.
function Numero({ planejado, realizado, mostrarRealizado = false, forte = false, tamanho = 18 }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.15 }}>
      <Typography component="span" sx={{ fontSize: tamanho, fontWeight: forte ? 800 : 500, color: '#111827' }}>
        {formatar(planejado)}
      </Typography>
      {mostrarRealizado && (
        <Typography component="span" title="Realizado na Agenda" sx={{ fontSize: 11.5, fontWeight: 600, color: '#6b7280' }}>
          real. {formatar(realizado)}
        </Typography>
      )}
    </Box>
  );
}

// O mês já começou (ou passou)? Meses futuros não mostram o "real.".
const MES_ATUAL = (() => { const h = new Date(); return h.getFullYear() * 12 + h.getMonth(); })();
const jaComecou = (l) => l.ano * 12 + (l.mes - 1) <= MES_ATUAL;

// Quadro no formato do modelo manual: título, ano/mês no cabeçalho, linhas Consultoria e Workshop e a coluna Total.
function QuadroCarga({ titulo, subtitulo, meses, linhas, totalConsultoria, totalWorkshop }) {
  const anos = [];
  meses.forEach((m) => {
    const ultimo = anos[anos.length - 1];
    if (ultimo && ultimo.ano === m.ano) ultimo.qtd += 1;
    else anos.push({ ano: m.ano, qtd: 1 });
  });
  const cabecalho = { backgroundColor: CINZA_CABECALHO, color: 'white', fontWeight: 700, textAlign: 'center', border: BORDA, px: 1, py: 0.8 };
  const celula = { border: BORDA, textAlign: 'center', backgroundColor: 'white', px: 1, py: 1, minWidth: 50 };
  const rotulo = { border: BORDA, backgroundColor: CINZA_ROTULO, fontWeight: 700, fontSize: 14, px: 1.5, py: 1, whiteSpace: 'nowrap', textAlign: 'left' };

  // A coluna Total é o que foi VENDIDO (carga contratada e workshops vendidos), não a soma dos meses.
  const celulaTotal = { ...celula, backgroundColor: '#f9fafb' };
  const linhaDeDados = (nome, planejada, realizada, total) => (
    <Box component="tr">
      <Box component="td" sx={rotulo}>{nome}</Box>
      {linhas.map((l, i) => (
        <Box component="td" key={i} sx={celula}>
          <Numero planejado={l[planejada]} realizado={l[realizada]} mostrarRealizado={jaComecou(l) && (l[planejada] > 0 || l[realizada] > 0)} />
        </Box>
      ))}
      <Box component="td" sx={celulaTotal}>
        <Numero planejado={total} forte tamanho={19} />
      </Box>
    </Box>
  );

  return (
    <Box sx={{ mb: 3, overflowX: 'auto' }}>
      <Box sx={{ minWidth: 820 }}>
        <Box sx={{ backgroundColor: AZUL, color: 'white', textAlign: 'center', py: 1.2, px: 2, borderRadius: '8px 8px 0 0' }}>
          <Typography sx={{ fontWeight: 800, fontSize: 17, color: 'white' }}>{titulo}</Typography>
          {subtitulo && <Typography sx={{ fontSize: 12, opacity: 0.8, color: 'white' }}>{subtitulo}</Typography>}
        </Box>
        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'auto' }}>
          <Box component="thead">
            <Box component="tr">
              <Box component="th" rowSpan={2} colSpan={2} sx={{ ...cabecalho, backgroundColor: CINZA_CABECALHO }} />
              {anos.map((a) => (
                <Box component="th" key={a.ano} colSpan={a.qtd} sx={{ ...cabecalho, fontSize: 15 }}>{a.ano}</Box>
              ))}
              <Box component="th" rowSpan={2} sx={{ ...cabecalho, fontSize: 15, minWidth: 84 }}>
                Total
                <Box component="span" sx={{ display: 'block', fontSize: 10.5, fontWeight: 500, opacity: 0.85 }}>vendido</Box>
              </Box>
            </Box>
            <Box component="tr">
              {meses.map((m) => (
                <Box component="th" key={`${m.ano}-${m.mes}`} sx={{ ...cabecalho, fontSize: 14 }}>{MESES_CURTOS[m.mes - 1]}</Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            <Box component="tr">
              <Box component="td" rowSpan={2} sx={{ ...rotulo, width: 28, p: 0, textAlign: 'center', backgroundColor: '#d1d5db' }}>
                <Box sx={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', fontWeight: 600, fontSize: 13, mx: 'auto' }}>dias</Box>
              </Box>
              <Box component="td" sx={rotulo}>Consultoria</Box>
              {linhas.map((l, i) => (
                <Box component="td" key={i} sx={celula}>
                  <Numero planejado={l.consultoria_planejada} realizado={l.consultoria_realizada} mostrarRealizado={jaComecou(l) && (l.consultoria_planejada > 0 || l.consultoria_realizada > 0)} />
                </Box>
              ))}
              <Box component="td" sx={celulaTotal}>
                <Numero planejado={totalConsultoria} forte tamanho={19} />
              </Box>
            </Box>
            {linhaDeDados('Workshop', 'workshop_planejado', 'workshop_realizado', totalWorkshop)}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// Vendido de vários projetos: projeto comum soma o seu; contrato com vários projetos entra UMA vez (a carga é do contrato).
function somarVendido(projetos) {
  const vistos = new Set();
  let dias = 0;
  let workshops = 0;
  projetos.forEach((p) => {
    if (p.contrato_id) {
      if (vistos.has(p.contrato_id)) return;
      vistos.add(p.contrato_id);
    }
    dias += Number(p.carga_contratada || 0);
    workshops += Number(p.workshops_vendidos || 0);
  });
  return { dias, workshops };
}

const somarMeses = (projetos, indice) => {
  const soma = (campo) => projetos.reduce((acc, p) => acc + Number(p.meses[indice][campo]), 0);
  return {
    consultoria_planejada: soma('consultoria_planejada'),
    consultoria_realizada: soma('consultoria_realizada'),
    workshop_planejado: soma('workshop_planejado'),
    workshop_realizado: soma('workshop_realizado'),
  };
};

// Controle de Carga: carga mês a mês de cada projeto (12 meses até o mês/ano escolhido no topo da tela).
// Respeita os filtros de Célula e de Projeto da tela. Escolher um projeto de contrato mostra os projetos do contrato.
export default function CargaMensalProjetos({ mes, ano, filtroCelula, opcaoProjeto }) {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState(false);

  useEffect(() => {
    let ativo = true;
    setErro(false);
    api.get('/carga-mensal-projetos/', { params: { mes, ano } })
      .then((r) => { if (ativo) setDados(r.data); })
      .catch(() => { if (ativo) { setErro(true); setDados(null); } });
    return () => { ativo = false; };
  }, [mes, ano]);

  let projetos = dados ? dados.projetos : [];
  if (filtroCelula) projetos = projetos.filter((p) => p.celula_ids.includes(filtroCelula));
  if (opcaoProjeto) {
    projetos = projetos.filter((p) => (opcaoProjeto.contratoId
      ? p.contrato_id === opcaoProjeto.contratoId
      : opcaoProjeto.chave === `p-${p.id}`));
  }

  return (
    <Card sx={{ mb: 3, borderRadius: 3 }}>
      <CardContent sx={{ p: 2.5 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Carga por mês de cada projeto</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          12 meses até {MESES_LONGOS[mes - 1]} de {ano} (escolha o mês e o ano no topo da tela). Número grande: planejado pelo Diretor. Em cinza: realizado na Agenda.
        </Typography>

        {erro && <Alert severity="error">Não foi possível carregar a carga por mês. Atualize a página; se continuar, avise o TI.</Alert>}
        {!erro && !dados && <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={26} /></Box>}
        {!erro && dados && projetos.length === 0 && (
          <Typography variant="body2" color="text.secondary">Nenhum projeto em andamento neste filtro.</Typography>
        )}

        {!erro && dados && projetos.length > 0 && (
          <>
            {projetos.length > 1 && (
              <QuadroCarga
                titulo="Carga de Consultoria — total dos projetos exibidos"
                subtitulo={`${projetos.length} projetos`}
                meses={dados.meses}
                linhas={dados.meses.map((_, i) => somarMeses(projetos, i))}
                totalConsultoria={somarVendido(projetos).dias}
                totalWorkshop={somarVendido(projetos).workshops}
              />
            )}
            {projetos.map((p) => (
              <QuadroCarga
                key={p.id}
                titulo={`Carga de Consultoria — ${p.nome}`}
                subtitulo={[p.cliente_nome, p.contrato_nome ? `Contrato: ${p.contrato_nome} (o total é do contrato todo)` : null].filter(Boolean).join(' · ')}
                meses={dados.meses}
                linhas={p.meses}
                totalConsultoria={p.carga_contratada}
                totalWorkshop={p.workshops_vendidos}
              />
            ))}
          </>
        )}
      </CardContent>
    </Card>
  );
}
